'use client';

import { type RefObject, useCallback, useEffect, useState } from 'react';

type FloatingListboxPlacement = 'top' | 'bottom';

export interface FloatingListboxPosition {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
  placement: FloatingListboxPlacement;
}

export interface UseFloatingListboxPositionOptions {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  preferredMaxHeight?: number;
  minHeight?: number;
  gap?: number;
  margin?: number;
}

interface CalculateFloatingListboxPositionOptions {
  rect: Pick<DOMRect, 'left' | 'top' | 'bottom' | 'width'>;
  viewportWidth: number;
  viewportHeight: number;
  preferredMaxHeight: number;
  minHeight: number;
  gap: number;
  margin: number;
}

export function calculateFloatingListboxPosition({
  rect,
  viewportWidth,
  viewportHeight,
  preferredMaxHeight,
  minHeight,
  gap,
  margin,
}: CalculateFloatingListboxPositionOptions): FloatingListboxPosition {
  const viewportMaxHeight = Math.max(48, viewportHeight - margin * 2);
  const availableBelow = Math.max(0, viewportHeight - rect.bottom - gap - margin);
  const availableAbove = Math.max(0, rect.top - gap - margin);
  const minAttachedHeight = Math.min(minHeight, 96);
  const openAbove = availableBelow < minAttachedHeight && availableAbove > availableBelow;
  const availableSpace = openAbove ? availableAbove : availableBelow;
  const maxHeight = Math.min(
    preferredMaxHeight,
    viewportMaxHeight,
    Math.max(minAttachedHeight, availableSpace),
  );
  const width = Math.max(0, Math.min(rect.width, viewportWidth - margin * 2));
  const left = Math.max(margin, Math.min(rect.left, viewportWidth - width - margin));
  const top = openAbove ? Math.max(margin, rect.top - gap - maxHeight) : rect.bottom + gap;

  return {
    left,
    top,
    width,
    maxHeight,
    placement: openAbove ? 'top' : 'bottom',
  };
}

export function useFloatingListboxPosition({
  isOpen,
  triggerRef,
  preferredMaxHeight = 240,
  minHeight = 96,
  gap = 4,
  margin = 8,
}: UseFloatingListboxPositionOptions) {
  const [position, setPosition] = useState<FloatingListboxPosition | null>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === 'undefined') return;

    const rect = trigger.getBoundingClientRect();
    setPosition(
      calculateFloatingListboxPosition({
        rect,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        preferredMaxHeight,
        minHeight,
        gap,
        margin,
      }),
    );
  }, [gap, margin, minHeight, preferredMaxHeight, triggerRef]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }

    updatePosition();
    const raf = window.requestAnimationFrame(updatePosition);
    const handleViewportChange = () => updatePosition();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen, updatePosition]);

  return { position, updatePosition };
}
